// src/finance/CalculatorPage.tsx
// Calculateur (spec § 2) : saisie à gauche, résultats sticky à droite.
// Recalcul 100 % local (moteur) ; autosave débouncé des settings et des dépenses.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { FinanceSettings } from '../lib/types'
import type { FinanceDirection } from './engine/types'
import { bestScenarioKey, computeScenarios, SCENARIO_LABELS, type Scenarios, type ScenarioKey } from './scenarios'
import { optimalSalaryShare } from './optimum'
import { appCommissionOf, appNetOf } from './serviceRevenue'
import {
  useFinanceSettings,
  useSaveFinanceSettings,
  useRecurringExpenses,
  useCreateRecurringExpense,
  useUpdateRecurringExpense,
  useDeleteRecurringExpense,
} from './api'
import { useAutosave, type AutosaveStatus } from './useAutosave'
import { diffRecurringExpenses, resolveLineKeys } from './expenseSync'
import { monthlyAmountOf } from './expenseMath'
import { revenueTimeline } from './timeline'
import { ExpenseListEditor, type EditableExpenseLine } from './components/ExpenseListEditor'
import { RevenueTimeline } from './components/RevenueTimeline'
import { ResultCard } from './components/ResultCard'
import { LivingBudget } from './components/LivingBudget'
import { JurisdictionCompare } from './components/JurisdictionCompare'
import { WhoPaysWhat } from './components/WhoPaysWhat'
import { TaxSettingsPanel } from './components/TaxSettingsPanel'
import { InfoTip } from './components/InfoTip'
import { TAX_INFO } from './components/taxInfo'
import { SaveBadge } from './components/SaveBadge'
import { fmtEur, parseAmount } from './format'

const pageStyle: React.CSSProperties = {
  padding: '1.5rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  gap: '1rem',
  alignItems: 'start',
  maxWidth: '1200px',
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'var(--mono)',
  padding: '0.5rem 0.625rem',
  width: '140px',
}

function toLine(e: {
  id: string
  label: string
  amount: number
  active: boolean
  periodicity: 'monthly' | 'yearly'
  dueMonth: number | null
}): EditableExpenseLine {
  return { key: e.id, label: e.label, amount: e.amount, active: e.active, periodicity: e.periodicity, dueMonth: e.dueMonth }
}

export function CalculatorPage() {
  const settingsQuery = useFinanceSettings()
  const expensesQuery = useRecurringExpenses()
  const saveSettings = useSaveFinanceSettings()
  const createExpense = useCreateRecurringExpense()
  const updateExpense = useUpdateRecurringExpense()
  const deleteExpense = useDeleteRecurringExpense()

  const [settings, setSettings] = useState<FinanceSettings | null>(null)
  const [companyLines, setCompanyLines] = useState<EditableExpenseLine[]>([])
  const [personalLines, setPersonalLines] = useState<EditableExpenseLine[]>([])
  const hydratedRef = useRef(false)
  // Liste serveur de référence pour le diff — mise à jour après chaque sync réussie.
  const serverExpensesRef = useRef<NonNullable<typeof expensesQuery.data>>([])
  // Chaîne unique partagée par les deux kinds (ils partagent `serverExpensesRef`).
  const syncChainRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    if (hydratedRef.current) return
    if (!settingsQuery.data || !expensesQuery.data) return
    hydratedRef.current = true
    setSettings(settingsQuery.data)
    serverExpensesRef.current = expensesQuery.data
    setCompanyLines(expensesQuery.data.filter((e) => e.kind === 'company').map(toLine))
    setPersonalLines(expensesQuery.data.filter((e) => e.kind === 'personal').map(toLine))
  }, [settingsQuery.data, expensesQuery.data])

  const settingsStatus = useAutosave(
    settings,
    (value) => (value ? saveSettings.mutateAsync(value) : Promise.resolve()),
    { enabled: settings !== null },
  )

  // Table key UI → id serveur. La key UI d'une ligne ne change JAMAIS après sa
  // création : elle sert de key React, et la remplacer (ancien « remap » du state)
  // démontait l'input pendant la frappe → perte du focus à chaque autosave d'une
  // nouvelle ligne (bug prod du 2026-08-20). La correspondance est résolue au
  // moment du sync via resolveLineKeys ; un snapshot périmé portant encore la key
  // temporaire est ainsi classé UPDATE, plus jamais re-CREATE (fin du churn).
  const serverIdByKeyRef = useRef(new Map<string, string>())

  const syncLines = async (lines: EditableExpenseLine[], kind: 'company' | 'personal') => {
    const serverOfKind = serverExpensesRef.current.filter((e) => e.kind === kind)
    const resolved = resolveLineKeys(lines, serverIdByKeyRef.current)
    const diff = diffRecurringExpenses(resolved, serverOfKind)
    for (const line of diff.creates) {
      const created = await createExpense.mutateAsync({
        kind, label: line.label, amount: line.amount, active: line.active,
        periodicity: line.periodicity, dueMonth: line.dueMonth,
      })
      // `line.key` est ici la key UI (aucune entrée dans la table avant création).
      serverIdByKeyRef.current.set(line.key, created.id)
      serverExpensesRef.current = [...serverExpensesRef.current, created]
    }
    for (const line of diff.updates) {
      const updated = await updateExpense.mutateAsync({
        id: line.key, label: line.label, amount: line.amount, active: line.active,
        periodicity: line.periodicity, dueMonth: line.dueMonth,
      })
      serverExpensesRef.current = serverExpensesRef.current.map((e) => (e.id === updated.id ? updated : e))
    }
    for (const id of diff.deletes) {
      await deleteExpense.mutateAsync(id)
      serverExpensesRef.current = serverExpensesRef.current.filter((e) => e.id !== id)
    }
  }

  // `useAutosave` n'annule que son timer, jamais une requête déjà partie : si un POST
  // dépasse les 800 ms de debounce, le sync suivant lirait `serverExpensesRef` avant
  // que le premier n'y ait poussé sa ligne créée → POST en double (l'orphelin persiste
  // si l'onglet se ferme, et compte deux fois dans le total société), et DELETE en
  // double → 404 → badge en erreur. On sérialise donc : chaque sync attend le
  // précédent. L'erreur remonte au statut d'autosave appelant mais ne casse pas la
  // chaîne (le maillon suivant repart d'un `catch` neutralisé).
  const enqueueSync = (kind: 'company' | 'personal') => (lines: EditableExpenseLine[]) => {
    const run = syncChainRef.current.catch(() => {}).then(() => syncLines(lines, kind))
    syncChainRef.current = run.then(
      () => undefined,
      () => undefined,
    )
    return run
  }

  const companyStatus = useAutosave(companyLines, enqueueSync('company'), {
    enabled: hydratedRef.current,
  })
  const personalStatus = useAutosave(personalLines, enqueueSync('personal'), {
    enabled: hydratedRef.current,
  })

  // Totaux MENSUALISÉS (les /an comptent /12) — seuls totaux que le moteur consomme.
  const companyTotal = useMemo(
    () => companyLines.filter((l) => l.active).reduce((sum, l) => sum + monthlyAmountOf(l), 0),
    [companyLines],
  )
  const personalTotal = useMemo(
    () => personalLines.filter((l) => l.active).reduce((sum, l) => sum + monthlyAmountOf(l), 0),
    [personalLines],
  )

  // Deux jeux complets : les cartes lisent `.uk`, le comparatif lit les deux.
  // En netToRevenue, le CA requis est réinversé PAR juridiction (pas un simple delta).
  // L'inatteignabilité est suivie PAR SCÉNARIO et par juridiction (cf. scenarios.ts).
  const regimes = useMemo(
    (): Scenarios | null => (settings ? computeScenarios(settings, companyTotal) : null),
    [settings, companyTotal],
  )

  // 101 simulations (dont 101 inversions en netToRevenue) : à mémoïser, sinon
  // elles repartent à chaque frappe. Les dépendances excluent volontairement
  // `mixSalaryShare` — l'optimum n'en dépend pas, et l'inclure relancerait tout
  // le balayage à CHAQUE cran du curseur, c'est-à-dire précisément quand
  // l'utilisateur s'en sert. Les autres champs de `settings` (payerMode,
  // payerRegime, billableDays, hoursPerDay) n'entrent pas non plus dans le
  // calcul ; direction, targetAmount et tax, si.
  const settingsDirection = settings?.direction
  const settingsTarget = settings?.targetAmount
  const settingsTax = settings?.tax
  const optimumShare = useMemo((): number | null => {
    if (!settings) return null
    return optimalSalaryShare(settings, companyTotal)?.share ?? null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsDirection, settingsTarget, settingsTax, companyTotal])

  // Les cartes affichent la juridiction UK : le badge se décide sur elle, et écarte
  // les scénarios hors de portée (cf. bestScenarioKey).
  const bestKey = useMemo(
    (): ScenarioKey | null =>
      settings && regimes ? bestScenarioKey(regimes, settings.direction, 'uk') : null,
    [settings, regimes],
  )

  // Scénarios réellement hors de portée dans la juridiction affichée : la bannière
  // les NOMME, au lieu d'affirmer qu'aucun CA n'atteint le net alors que deux
  // cartes sur trois affichent un CA parfaitement atteignable.
  const unreachableLabels = useMemo((): string[] => {
    if (!regimes) return []
    return (['fullDividends', 'fullSalary', 'mix'] as ScenarioKey[])
      .filter((key) => regimes.unreachable.uk[key])
      .map((key) => SCENARIO_LABELS[key])
  }, [regimes])

  // ⚠️ erreur AVANT chargement : en cas d'échec, settings reste null et le
  // check de chargement afficherait « Chargement… » pour toujours.
  // `!data` : un refetch raté après un save ne doit pas tuer la page — React Query
  // garde les données déjà chargées, l'état local et l'autosave restent opérants.
  if ((settingsQuery.isError && !settingsQuery.data) || (expensesQuery.isError && !expensesQuery.data)) {
    return (
      <div style={{ padding: '2rem', color: 'var(--arancio)', fontSize: '0.875rem' }}>
        Erreur de chargement des données Finance.
      </div>
    )
  }
  if (settingsQuery.isLoading || expensesQuery.isLoading || !settings || !regimes) {
    return (
      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Chargement du calculateur…
      </div>
    )
  }

  // Priorité erreur > enregistrement > enregistré > idle sur les TROIS statuts : une
  // modification qui ne touche que les dépenses doit aussi afficher « Enregistré ».
  const statuses = [settingsStatus, companyStatus, personalStatus]
  const globalStatus: AutosaveStatus = statuses.includes('error')
    ? 'error'
    : statuses.includes('saving')
      ? 'saving'
      : statuses.includes('saved')
        ? 'saved'
        : 'idle'

  const patchSettings = (changes: Partial<FinanceSettings>) =>
    setSettings((previous) => (previous ? { ...previous, ...changes } : previous))

  const direction: FinanceDirection = settings.direction

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1.5rem 1.5rem 0' }}>
        <h1 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--fg)', margin: 0 }}>Calculateur</h1>
        <SaveBadge status={globalStatus} />
      </header>

      {unreachableLabels.length > 0 && (
        <div
          data-testid="calculator-unreachable"
          style={{
            margin: '1rem 1.5rem 0',
            maxWidth: '1200px',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid var(--arancio)',
            color: 'var(--arancio)',
            fontSize: '0.875rem',
          }}
        >
          Cible inatteignable avec ces paramètres pour {unreachableLabels.join(', ')} : aucun chiffre
          d’affaires n’y atteint ce net. Vérifiez les taux du panneau fiscal.
        </div>
      )}

      {/* CA du scénario MIX en UK. Les objectifs du Suivi suivent, eux, le scénario
          et la juridiction sélectionnés dans « Qui paie quoi » : les deux coïncident
          tant que ce sélecteur est resté sur « Mix » + « UK ». */}
      <div style={{ padding: '1rem 1.5rem 0', maxWidth: '1200px' }}>
        {/* Sur la PRESTATION seule : un TJM ou un CA horaire calculé sur un total
            incluant des abonnements ne décrirait plus le temps facturé. */}
        <RevenueTimeline
          values={revenueTimeline(regimes.serviceRevenue.uk.mix, settings.billableDays, settings.hoursPerDay)}
          billableDays={settings.billableDays}
          hoursPerDay={settings.hoursPerDay}
        />
      </div>

      <div style={pageStyle}>
        {/* ── Colonne saisie ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <section style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>Objectif</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {(
                [
                  ['netToRevenue', 'Net cible → CA'],
                  ['revenueToNet', 'CA → Net'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => patchSettings({ direction: value })}
                  style={{
                    background: direction === value ? 'var(--elevated)' : 'transparent',
                    border: `1px solid ${direction === value ? 'var(--line-strong)' : 'var(--line)'}`,
                    borderRadius: '6px',
                    color: direction === value ? 'var(--fg)' : 'var(--muted)',
                    fontSize: '0.8125rem',
                    fontFamily: 'inherit',
                    padding: '0.375rem 0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                {direction === 'netToRevenue' ? 'Net perso mensuel visé' : 'CA mensuel facturé'}
                <input
                  style={inputStyle}
                  key={`target-${direction}-${settings.targetAmount}`}
                  defaultValue={String(settings.targetAmount)}
                  inputMode="decimal"
                  onBlur={(e) => {
                    const parsed = parseAmount(e.target.value)
                    if (parsed !== null) patchSettings({ targetAmount: parsed })
                    else e.target.value = String(settings.targetAmount)
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                Jours facturables / mois
                <input
                  style={{ ...inputStyle, width: '80px' }}
                  defaultValue={String(settings.billableDays)}
                  inputMode="numeric"
                  onBlur={(e) => {
                    const parsed = parseAmount(e.target.value)
                    if (parsed !== null && parsed <= 31) patchSettings({ billableDays: parsed })
                    else e.target.value = String(settings.billableDays)
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                Heures facturables / jour
                <input
                  style={{ ...inputStyle, width: '80px' }}
                  defaultValue={String(settings.hoursPerDay)}
                  inputMode="numeric"
                  onBlur={(e) => {
                    const parsed = parseAmount(e.target.value)
                    if (parsed !== null && parsed <= 24) patchSettings({ hoursPerDay: parsed })
                    else e.target.value = String(settings.hoursPerDay)
                  }}
                />
              </label>
            </div>
          </section>

          {/* ── Revenus de l'app ──
              Apple est fournisseur réputé : il collecte les taxes de vente dans
              chaque pays et prélève sa commission avant de reverser. On saisit donc
              le BRUT payé par les utilisateurs, et la commission devient une charge
              d'exploitation — la seule lecture cohérente avec la comptabilisation
              en brut retenue par le comptable. */}
          <section style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>Revenus de l’app</h2>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                Encaissé par les utilisateurs
                <input
                  style={inputStyle}
                  defaultValue={String(settings.appRevenueGross)}
                  inputMode="decimal"
                  onBlur={(e) => {
                    const parsed = parseAmount(e.target.value)
                    if (parsed !== null) patchSettings({ appRevenueGross: parsed })
                    else e.target.value = String(settings.appRevenueGross)
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  Commission Apple (%)
                  <InfoTip label="Commission Apple" text={TAX_INFO.appCommissionSetting} />
                </span>
                <input
                  style={{ ...inputStyle, width: '80px' }}
                  defaultValue={String(Math.round(settings.appCommissionRate * 1000) / 10)}
                  inputMode="decimal"
                  onBlur={(e) => {
                    const parsed = parseAmount(e.target.value)
                    if (parsed !== null && parsed >= 0 && parsed <= 100) patchSettings({ appCommissionRate: parsed / 100 })
                    else e.target.value = String(Math.round(settings.appCommissionRate * 1000) / 10)
                  }}
                />
              </label>
            </div>
            {settings.appRevenueGross > 0 && (
              <div data-testid="app-net" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', borderTop: '1px solid var(--line)', paddingTop: '0.5rem' }}>
                <span style={{ color: 'var(--muted)' }}>
                  Commission <span style={{ fontFamily: 'var(--mono)', color: 'var(--bronze)' }}>−{fmtEur(appCommissionOf(settings))}</span> — versé par Apple
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fg)', fontWeight: 600 }}>
                  {fmtEur(appNetOf(settings))}
                </span>
              </div>
            )}
          </section>

          <ExpenseListEditor
            title="Dépenses société"
            lines={companyLines}
            onChange={setCompanyLines}
            showActiveToggle
            showPeriodicity
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <ExpenseListEditor
              title="Dépenses perso"
              lines={personalLines}
              onChange={setPersonalLines}
              showActiveToggle
              showPeriodicity
            />
            <button
              onClick={() => patchSettings({ direction: 'netToRevenue', targetAmount: personalTotal })}
              disabled={personalTotal <= 0}
              style={{
                alignSelf: 'flex-start',
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: personalTotal > 0 ? 'var(--muted)' : 'var(--dim)',
                fontSize: '0.75rem',
                fontFamily: 'inherit',
                padding: '0.375rem 0.75rem',
                cursor: personalTotal > 0 ? 'pointer' : 'default',
              }}
            >
              Utiliser {fmtEur(personalTotal)} comme net cible
            </button>
          </div>

          <TaxSettingsPanel tax={settings.tax} onChange={(tax) => patchSettings({ tax })} />
        </div>

        {/* ── Colonne résultats (sticky) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '1.5rem' }}>
          <ResultCard
            title={SCENARIO_LABELS.fullDividends}
            regime={regimes.uk.fullDividends}
            direction={direction}
            isBest={bestKey === 'fullDividends'}
            unreachable={regimes.unreachable.uk.fullDividends}
            personalExpensesMonthly={personalTotal}
          />
          <ResultCard
            title={SCENARIO_LABELS.fullSalary}
            regime={regimes.uk.fullSalary}
            direction={direction}
            isBest={bestKey === 'fullSalary'}
            unreachable={regimes.unreachable.uk.fullSalary}
            personalExpensesMonthly={personalTotal}
          />
          <ResultCard
            title={SCENARIO_LABELS.mix}
            regime={regimes.uk.mix}
            direction={direction}
            isBest={bestKey === 'mix'}
            unreachable={regimes.unreachable.uk.mix}
            personalExpensesMonthly={personalTotal}
            slider={{
              value: settings.mixSalaryShare,
              onChange: (v) => patchSettings({ mixSalaryShare: v }),
              optimum: optimumShare,
            }}
          />
          {/* Scénario mix, comme la timeline de CA et les objectifs du Suivi.
              Le libellé le dit : le donut de « Qui paie quoi », juste en dessous,
              suit son propre sélecteur et affiche un autre « ce qui te reste ». */}
          <LivingBudget
            netMonthly={regimes.uk.mix.netMonthly}
            personalExpensesMonthly={personalTotal}
            scenarioLabel="Mix salaire + dividendes, UK"
          />
          <JurisdictionCompare
            direction={direction}
            rows={(['fullDividends', 'fullSalary', 'mix'] as ScenarioKey[]).map((key) => ({
              label: SCENARIO_LABELS[key],
              uk: regimes.uk[key],
              fr: regimes.fr[key],
              unreachable: { uk: regimes.unreachable.uk[key], fr: regimes.unreachable.fr[key] },
            }))}
          />
          <WhoPaysWhat
            direction={direction}
            regimes={regimes}
            personalExpensesMonthly={personalTotal}
            appCommissionMonthly={appCommissionOf(settings)}
            mode={settings.payerMode}
            regime={settings.payerRegime}
            unreachable={regimes.unreachable[settings.payerRegime][settings.payerMode]}
            onModeChange={(payerMode) => patchSettings({ payerMode })}
            onRegimeChange={(payerRegime) => patchSettings({ payerRegime })}
          />
          <p style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0 }}>
            Estimations indicatives — à valider avec ton comptable.
          </p>
        </div>
      </div>
    </div>
  )
}
